import { PersonAddOutlined } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Tooltip } from "@mui/material";
import { useTranslate } from "@refinedev/core";
import type { BaseKey } from "@refinedev/core";
import { ButtonProps } from "@mui/material";
import axios from "axios";
import { config } from "config";
import { dataProvider } from "rest-data-provider";
import { fetchLatestOrcaDeployActivityLogRow } from "rest-data-provider/utils/orcaDeployActivityLogsTypes";
import type { OrcaProdUrlEntry } from "rest-data-provider/utils/orcaDeployActivityLogsTypes";
import React, { useState } from "react";


export type ImpersonateButtonProps = ButtonProps & {
  resource?: string;
  recordItemId?: BaseKey;
  record?: any;
  onSuccess?: () => void;
  hideText?: boolean;
  confirmTitle?: string;
  confirmOkText?: string;
  confirmCancelText?: string;
  meta?: any;
  svgIconProps?: any;
};

enum UserType {
  CRM = 0,
  CHATROOM = 1,
}

export const ImpersonateButton: React.FC<ImpersonateButtonProps> = ({
  record,
  onSuccess,
  children,
  hideText = false,
  meta,
  confirmTitle,
  confirmOkText,
  confirmCancelText,
  svgIconProps,
  ...rest
}) => {
  const translate = useTranslate();

  const [open, setOpen] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [destinationOpen, setDestinationOpen] = useState(false);
  const [impersonationToken, setImpersonationToken] = useState<string | null>(null);
  const [simEntries, setSimEntries] = useState<OrcaProdUrlEntry[]>([]);
  const [crmIdMap, setCrmIdMap] = useState<Record<number, string> | null>(null);

  const user = record;

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleConfirm = async () => {
    if (!user) return;

    setIsImpersonating(true);
    setOpen(false);

    try {
      const cfg = config();
      const apiUrl = cfg.server.api;
      const currentToken = sessionStorage.getItem("authorization");
      let currentCrmIdMap = crmIdMap;

      if (!currentCrmIdMap) {
        const prodCategoriesResponse = await dataProvider(apiUrl).getList({
          resource: "prod-categories",
          pagination: { current: 1, pageSize: 1000 },
          sorters: [],
          filters: [],
        });

        const prodCategories = prodCategoriesResponse.data || [];
        currentCrmIdMap = {};
        prodCategories.forEach((cat: any) => {
          const categoryId = Number(cat.id);
          currentCrmIdMap![categoryId] = cat.crmId;
        });

        setCrmIdMap(currentCrmIdMap);
      }

      let userProdCategoryIds: number[] = [];
      if (typeof user.prodCategories === 'string' && user.prodCategories) {
        userProdCategoryIds = user.prodCategories.split(',').map((id: string) => parseInt(id.trim())).filter((id: number) => !isNaN(id));
      }

      const latestDeployLogRow = await fetchLatestOrcaDeployActivityLogRow(apiUrl);
      const currentSimEntries = latestDeployLogRow?.result ?? [];
      const prodCategoriesCrmIds = userProdCategoryIds
        .map((id: number) => currentCrmIdMap?.[id])
        .filter(Boolean);

      const currentUser = JSON.parse(sessionStorage.getItem("user") || "{}");
      const response = await axios.post(`${apiUrl}/v2/admin/chat-token`, {
        payload: {
          email: user.email,
          username: user.email,
          nickname: user.nickname,
          isImpersonate: true,
          userType: user.userType,
          prodCategories: prodCategoriesCrmIds.length > 0 ? prodCategoriesCrmIds : [""],
          accountId: user.uuid,
          isProTrader: false,
          isLoginFromHermes: user.userType === UserType.CHATROOM,
          adminUserId: currentUser.id
        }
      }, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      });

      const token = response.data.token;
      setImpersonationToken(token);
      setSimEntries(currentSimEntries);
      setDestinationOpen(true);
    } catch (error) {
      console.error("Impersonation failed:", error);
      alert("Impersonation failed. Please try again.");
    } finally {
      setIsImpersonating(false);
    }
  };

  const handleGotoChatroom = () => {
    if (!impersonationToken) return;
    const chatroomUrl = `${config().server.chatroom}/sso?data=${encodeURIComponent(impersonationToken)}`;
    window.open(chatroomUrl, "_blank");
    setDestinationOpen(false);
    setImpersonationToken(null);
  };

  const handleGotoSim = (entry: OrcaProdUrlEntry) => {
    if (!impersonationToken) return;
    const simUrl = `${entry.server}${encodeURIComponent(impersonationToken)}`;
    window.open(simUrl, "_blank");
    setDestinationOpen(false);
    setImpersonationToken(null);
  };

  const handleDestinationClose = () => {
    setDestinationOpen(false);
    setImpersonationToken(null);
  };

  const { sx, ...restProps } = rest;

  const buttonColor = user?.userType === UserType.CHATROOM ? "primary" : "secondary";

  return (
    <div>
      <Tooltip title="Impersonate User">
        <LoadingButton
          color={buttonColor}
          onClick={handleClickOpen}
          loading={isImpersonating}
          startIcon={!hideText && <PersonAddOutlined {...svgIconProps} />}
          sx={{ minWidth: 0, ...sx }}
          loadingPosition={hideText ? "center" : "start"}
          {...restProps}
        >
          {hideText ? (
            <PersonAddOutlined fontSize="small" {...svgIconProps} />
          ) : (
            children ?? translate("buttons.impersonate", "Impersonate")
          )}
        </LoadingButton>
      </Tooltip>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="impersonate-dialog-title"
        aria-describedby="impersonate-dialog-description"
      >
        <DialogTitle id="impersonate-dialog-title">
          {confirmTitle ?? "Are you sure you want to impersonate this user?"}
        </DialogTitle>
        <DialogActions sx={{ justifyContent: "center" }}>
          <Button onClick={handleClose}>
            {confirmCancelText ?? translate("buttons.cancel", "Cancel")}
          </Button>
          <Button color="primary" onClick={handleConfirm} autoFocus>
            {confirmOkText ?? "Impersonate"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={destinationOpen}
        onClose={handleDestinationClose}
        aria-labelledby="destination-dialog-title"
      >
        <DialogTitle id="destination-dialog-title">
          Choose destination
        </DialogTitle>
        <DialogContent />
        <DialogActions sx={{ justifyContent: "center", gap: 1 }}>
          <Button onClick={handleDestinationClose}>
            {translate("buttons.cancel", "Cancel")}
          </Button>
          <Button color="primary" variant="contained" onClick={handleGotoChatroom}>
            Chatroom
          </Button>
          {simEntries[0] && (
            <Button color="secondary" variant="contained" onClick={() => handleGotoSim(simEntries[0])}>
              Sim Prod
            </Button>
          )}
          {simEntries[1] && (
            <Button color="secondary" variant="contained" onClick={() => handleGotoSim(simEntries[1])}>
              Sim Second
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
};
